import { Component, DestroyRef, inject, output, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ConfirmationModalComponent } from '../../../../parts/confirmation-modal/confirmation-modal.component';
import { InfoModalComponent } from '../../../../parts/info-modal/info-modal.component';
import { ModalActions, ModalTypes } from '../../../../core/enums/base.enums';
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { ChargesService } from '../../../../core/services/charge/charge.service';
import {
    DeletePropertyChargeScheduleRequest,
    PropertyChargeScheduleModel
} from '../../../../core/models/data/property-charge-schedule.model';
import { BalancesMessagingConstants, GenericMessagingConstants } from '../../../../core/constants/messaging.constants';
import { BaseFormComponent } from '../../../../core/base-classes/base-form-component';
import { EscCloseDirective } from '../../../../core/directives/esc-close.directive';
import { InputLabel } from '../../../../parts/input-label/input-label.component';
import { CanComponentDeactivate } from '../../../../core/routing/can-deactivate.guard';
import { DirtyFormRegistryService } from '../../../../core/services/dirty-form-registry.service';

@Component({
    selector: 'ms-delete-invoice-schedule-modal',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FontAwesomeModule,
        ConfirmationModalComponent,
        InfoModalComponent,
        EscCloseDirective,
        InputLabel
    ],
    templateUrl: './delete-invoice-schedule-modal.component.html'
})
export class DeleteInvoiceScheduleModalComponent extends BaseFormComponent implements CanComponentDeactivate {

    //#region Properties & Signals

    public deleteCompleted = output<PropertyChargeScheduleModel>();

    public scheduleId = signal<number | undefined>(undefined);
    public scheduleName = signal<string | undefined>(undefined);

    public BalancesMessagingConstants = BalancesMessagingConstants;

    public deleteForm: FormGroup;

    @ViewChild('deleteInvoiceScheduleInfoRef') infoRef!: InfoModalComponent;
    @ViewChild('deleteInvoiceScheduleEscConfirmationRef') escConfirmationRef!: ConfirmationModalComponent;

    //#endregion Properties & Signals

    //#region Constructor

    private fb = inject(FormBuilder);
    private chargesService = inject(ChargesService);
    private bootstrapService = inject(BootstrapService);
    private destroyRef = inject(DestroyRef);
    private dirtyFormRegistry = inject(DirtyFormRegistryService);

    constructor() {
        super();

        this.destroyRef.onDestroy(() => this.dirtyFormRegistry.unregister(this));

        this.deleteForm = this.fb.group({
            voidOpenAndLateInvoices: [false],
            sendEmailNotifications: [{ value: false, disabled: true }],
            note: ['', [Validators.maxLength(255)]]
        });

        // Keep sendEmailNotifications gated on voidOpenAndLateInvoices
        const voidCtrl = this.deleteForm.get('voidOpenAndLateInvoices')!;
        const notifyCtrl = this.deleteForm.get('sendEmailNotifications')!;
        voidCtrl.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((voidChecked: boolean) => {
                if (voidChecked) {
                    notifyCtrl.enable({ emitEvent: false });
                } else {
                    notifyCtrl.setValue(false, { emitEvent: false });
                    notifyCtrl.disable({ emitEvent: false });
                }
            });
    }

    //#endregion Constructor

    //#region Public Methods

    public open(scheduleId: number, scheduleName?: string): void {
        this.scheduleId.set(scheduleId);
        this.scheduleName.set(scheduleName);
        this.resetForm();
        this.dirtyFormRegistry.register(this);
        this.bootstrapService.manageModal('#deleteInvoiceScheduleModal', ModalActions.Show);
    }

    //#endregion Public Methods

    //#region Form Submission

    public async onSubmit(): Promise<void> {
        if (this.deleteForm.invalid) {
            this.deleteForm.markAllAsTouched();
            return;
        }

        const scheduleId = this.scheduleId();
        if (scheduleId === undefined) return;

        this.isSubmitting = true;

        try {
            const request = this.buildRequest();
            const result = await this.chargesService.deletePropertyChargeSchedule(scheduleId, request);

            this.bootstrapService.manageModal('#deleteInvoiceScheduleModal', ModalActions.Hide);

            if (result.success && result.data) {
                this.submitSucceeded = true;
                this.infoRef.modalType.set(ModalTypes.Success);
                this.infoRef.message.set(BalancesMessagingConstants.PROPERTY_CHARGE_SCHEDULE_DELETE_SUCCESS());
                this.bootstrapService.manageModal('#deleteInvoiceScheduleInfoModal', ModalActions.Show);
                this.deleteCompleted.emit(result.data);
            } else {
                this.infoRef.modalType.set(ModalTypes.Failure);
                this.infoRef.message.set(
                    result.message || BalancesMessagingConstants.PROPERTY_CHARGE_SCHEDULE_DELETE_FAILURE()
                );
                this.bootstrapService.manageModal('#deleteInvoiceScheduleInfoModal', ModalActions.Show);
            }
        } catch {
            this.bootstrapService.manageModal('#deleteInvoiceScheduleModal', ModalActions.Hide);
            this.infoRef.modalType.set(ModalTypes.Failure);
            this.infoRef.message.set(BalancesMessagingConstants.PROPERTY_CHARGE_SCHEDULE_DELETE_FAILURE());
            this.bootstrapService.manageModal('#deleteInvoiceScheduleInfoModal', ModalActions.Show);
        } finally {
            this.isSubmitting = false;
        }
    }

    //#endregion Form Submission

    //#region Modal Management

    public onCancel(): void {
        if (this.deleteForm.dirty) {
            this.escConfirmationRef.modalType.set(ModalTypes.Question);
            this.escConfirmationRef.message.set(GenericMessagingConstants.GENERIC_ABORT_CONFIRMATION());
            this.bootstrapService.manageModal('#deleteInvoiceScheduleModal', ModalActions.Hide);
            this.bootstrapService.manageModal('#deleteInvoiceScheduleEscConfirmationModal', ModalActions.Show);
        } else {
            this.bootstrapService.manageModal('#deleteInvoiceScheduleModal', ModalActions.Hide);
            this.dirtyFormRegistry.unregister(this);
        }
    }

    public infoModalConfirmed(): void {
        this.bootstrapService.manageModal('#deleteInvoiceScheduleInfoModal', ModalActions.Hide);
        this.dirtyFormRegistry.unregister(this);
    }

    public canDeactivate(): boolean | Promise<boolean> {
        return this.awaitLeaveConfirmation(this.deleteForm.dirty && !this.submitSucceeded, () => {
            this.escConfirmationRef.modalType.set(ModalTypes.Question);
            this.escConfirmationRef.message.set(GenericMessagingConstants.GENERIC_ABORT_CONFIRMATION());
            this.bootstrapService.manageModal('#deleteInvoiceScheduleModal', ModalActions.Hide);
            this.bootstrapService.manageModal('#deleteInvoiceScheduleEscConfirmationModal', ModalActions.Show);
        });
    }

    public onEscConfirmed(): void {
        this.bootstrapService.manageModal('#deleteInvoiceScheduleEscConfirmationModal', ModalActions.Hide);
        this.dirtyFormRegistry.unregister(this);
        this.resolvePendingLeave(true);
    }

    public onEscCancelled(): void {
        this.bootstrapService.manageModal('#deleteInvoiceScheduleEscConfirmationModal', ModalActions.Hide);
        this.bootstrapService.manageModal('#deleteInvoiceScheduleModal', ModalActions.Show);
        this.resolvePendingLeave(false);
    }

    //#endregion Modal Management

    //#region Private Helpers

    private buildRequest(): DeletePropertyChargeScheduleRequest {
        const formValue = this.deleteForm.getRawValue();
        return {
            voidOpenAndLateInvoices: !!formValue.voidOpenAndLateInvoices,
            sendEmailNotifications: !!formValue.voidOpenAndLateInvoices && !!formValue.sendEmailNotifications,
            note: formValue.note?.trim() || undefined
        };
    }

    private resetForm(): void {
        this.submitSucceeded = false;
        this.deleteForm.reset({
            voidOpenAndLateInvoices: false,
            sendEmailNotifications: false,
            note: ''
        });
        this.deleteForm.get('sendEmailNotifications')?.disable({ emitEvent: false });
    }

    //#endregion Private Helpers
}
